import React, { useCallback, useEffect, useMemo, useState } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import Select from "react-select";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TableSkelton from "../../shared/TableSkelton";
import API from "../../services/api";
import NoDataFound from "../../shared/NoDataFound";
import { Toast } from "../../shared/Toast";
import { toLakhs } from "../../utils/constants";

const hourLabel = (h) => `${String(h).padStart(2, "0")} - ${String(h + 1).padStart(2, "0")} Hours`;

const NetworkReportHourly = () => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [position, setPosition] = useState({ value: 1, label: "1" });
  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [compareDates, setCompareDates] = useState({ date1: null, date2: null });
  const [compareData, setCompareData] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  let positionOptions =[{value:-1,label:'All'}];
  for(let i=0;i<=100;i++) positionOptions.push({value:i,label:`${i}`})

  const formatDate = (date) => {
    if (!date) return null;
    date = new Date(date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    date = `${year}-${month}-${day}`;
    return date;
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  function getStartAndEndOfMonth(d) {
    const startDate = new Date(d.getFullYear(), d.getMonth(), 1);
    const formattedStartDate = formatDate(startDate);

    const endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const formattedEndDate = formatDate(endDate);

    return { startDate: formattedStartDate, endDate: formattedEndDate };
  }

  const fetchData = async () => {
    if (!selectedMonth) return;
    const { startDate, endDate } = getStartAndEndOfMonth(selectedMonth);
    if (!startDate || !endDate) return;

    setTableLoading(true);
    const res = await API({
      url: "/adresponses/hourly",
      method: "POST",
      responseType: "json",
      data: {
        startDate,
        endDate,
        position: position?.value ?? null,
      },
    });
    if (res.status === "SUCCESS") setData(res?.data ?? []);
    else Toast("error", "SOMETHING WENT WRONG");
    setTableLoading(false);
  };

  const fetchCompareData = async () => {
    if (!compareDates.date1 || !compareDates.date2) return;
    const date1 = formatDate(compareDates.date1);
    const date2 = formatDate(compareDates.date2);
    setTableLoading(true);
    const newCompareData = await API({
      url: "/adresponses/hourly",
      method: "POST",
      responseType: "json",
      data: {
        compareDates: { date1, date2 },
        position: position?.value ?? null,
      },
    });
    if (newCompareData.status === "SUCCESS") setCompareData(newCompareData.data);
    else {
      setCompareData([]);
      Toast("error", "Something Went Wrong");
    }
    setTableLoading(false);
  };

  useEffect(() => {
    if (showCompare && compareDates.date1 && compareDates.date2) {
      fetchCompareData();
    }
  }, [showCompare, compareDates]);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, position]);

  const zeroHourObj = () => ({
    req_count: 0,
    response_count: 0,
    error_count: 0,
    display_count: 0,
  });
  const restructureData = (raw = []) => {
    const result = {};
    raw?.forEach((item) => {
      const { date, daily_metrics } = item || {};
      if (!date) return;
      if (!result[date]) {
        result[date] = Array.from({ length: 24 }, () => ({ ...zeroHourObj() }));
      }
      daily_metrics?.forEach((metric) => {
        const {hour, request, response, revenue} = metric;
        const h = Number(hour);
        if (Number.isInteger(h) && h >= 0 && h < 24) {
          result[date][h] = {
            req_count: parseInt(request, 10) || 0,
            response_count: parseInt(response, 10) || 0,
            error_count:  parseInt(revenue, 10) || 0,
          };
        }
      });
    });
    return result;
  };

  const compareStructuredData = [];
  compareData.forEach((data) => {
    const date = data.length > 0 && data[0]?.date ? data[0].date : null;
    data = restructureData(data);
    compareStructuredData.push({ date, data });
  });
  const structuredData = useMemo(() => restructureData(data), [data]);
  const dates = useMemo(() => Object.keys(structuredData).sort(), [structuredData]);

  const dateTotals = useMemo(() => {
    return dates.map((date) => {
      const total = { ...zeroHourObj() };
      const dateData = structuredData[date] || [];
      dateData.forEach((hourData) => {
        total.req_count += hourData?.req_count ?? 0;
        total.response_count += hourData?.response_count ?? 0;
        total.error_count += hourData?.error_count ?? 0;
      });
      return total;
    });
  }, [dates, structuredData]);

  const grandTotals = useMemo(() => {
    const hours = Array.from({ length: 24 }, () => ({ ...zeroHourObj() }));
    dates.forEach((date) => {
      const dateData = structuredData[date] || [];
      for (let h = 0; h < 24; h++) {
        const hd = dateData[h] || zeroHourObj();
        hours[h].req_count += hd.req_count;
        hours[h].response_count += hd.response_count;
        hours[h].error_count += hd.error_count;
      }
    });
    return hours;
  }, [dates, structuredData]);

  const overallTotals = useMemo(() => {
    return dateTotals.reduce(
      (acc, t) => ({
        req_count: acc.req_count + (t?.req_count ?? 0),
        response_count: acc.response_count + (t?.response_count ?? 0),
        error_count: acc.error_count + (t?.error_count ?? 0),
        display_count: acc.display_count + (t?.display_count ?? 0),
      }),
      { ...zeroHourObj() }
    );
  }, [dateTotals]);

  const csvData = useMemo(() => {
    const headers = [
      "Date",
      ...[...Array(24).keys()].flatMap((hour) => [`${hourLabel(hour)} Request`, `${hourLabel(hour)} Impressions`, `${hourLabel(hour)} Revenue`]),
      "Total Request",
      "Total Impressions",
      "Total Revenue",
    ];

    const rows = dates.map((date, rowIndex) => {
      const row = [date];
      [...Array(24).keys()].forEach((hour) => {
        const { req_count, response_count, error_count} = structuredData[date][hour] ?? zeroHourObj();
        row.push(req_count, response_count, error_count);
      });
      row.push(dateTotals[rowIndex]?.req_count ?? 0, dateTotals[rowIndex]?.response_count ?? 0, dateTotals[rowIndex]?.error_count ?? 0);
      return row;
    });

    const totalRow = ["Total"];
    grandTotals.forEach((hour) => {
      totalRow.push(hour.req_count, hour.response_count, hour.error_count);
    });
    totalRow.push(overallTotals.req_count, overallTotals.response_count, overallTotals.error_count);

    return [headers, ...rows, totalRow];
  }, [dates, structuredData, dateTotals, grandTotals, overallTotals]);

  const COLS = 1 + 24 * 3 + 3;

  const renderCell = (val, compareVal = null) => {
    let indicator = null;
    let cellClass = "";
    
    if (compareVal !== null) {
      if (val > compareVal) {
        indicator = <span className="inline-block w-2 h-2 rounded-full bg-green-500 ml-1"></span>;
        cellClass = "bg-green-50 text-green-800";
      } else if (val < compareVal) {
        indicator = <span className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1"></span>;
        cellClass = "bg-red-50 text-red-800";
      }
    }
    
    return (
      <span title={val} className={`flex items-center justify-center px-2 py-1 rounded ${cellClass}`}>
        {toLakhs(val ?? 0)}
        {indicator}
      </span>
    );
  };

  const toggleCompare = () => {
    setShowCompare(!showCompare);
    if (!showCompare) {
      setCompareDates({ date1: null, date2: null });
      setCompareData([]);
    }
  };

  return (
    <div className="p-4 bg-white min-h-screen">
      <div className="rounded-2xl overflow-hidden p-2">
        <div className="flex flex-col sm:flex-row sm:items-end mb-3 gap-4">
          <h1 className="text-xl font-semibold">Network Report Hourly</h1>
          <div className="ml-auto flex flex-wrap items-end gap-4">
            <div className="w-full sm:w-40">
              <button onClick={toggleCompare} className={`w-full py-2 px-4 rounded-lg ${showCompare ? "bg-red-500" : "bg-cyan-500"} text-white font-medium text-sm`}>
                {showCompare ? "Cancel Compare" : "Compare Dates"}
              </button>
            </div>
            {showCompare && (
              <div className="flex flex-wrap gap-4">
                <div className="w-full sm:w-40">
                  <label htmlFor="compare-date1" className="block text-sm font-medium mb-1">
                    Compare Date 1
                  </label>
                  <DatePicker
                    selected={compareDates.date1}
                    onChange={(date) => setCompareDates({ ...compareDates, date1: date })}
                    dateFormat="yyyy-MM-dd"
                    className="dateChooser border border-gray-300 rounded-md px-2 py-1 text-sm w-full"
                  />
                </div>
                <div className="w-full sm:w-40">
                  <label htmlFor="compare-date2" className="block text-sm font-medium mb-1">
                    Compare Date 2
                  </label>
                  <DatePicker
                    selected={compareDates.date2}
                    onChange={(date) => setCompareDates({ ...compareDates, date2: date })}
                    dateFormat="yyyy-MM-dd"
                    className="dateChooser border border-gray-300 rounded-md px-2 py-1 text-sm w-full"
                  />
                </div>
              </div>
            )}
            <div className="w-full sm:w-40">
              <label htmlFor="month-select" className="block text-sm font-medium mb-1">
                Month
              </label>
              <DatePicker
                selected={selectedMonth}
                onChange={handleMonthChange}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                className="dateChooser border border-gray-300 rounded-md px-2 py-1 text-sm w-full"
              />
            </div>
            <div className="w-full sm:w-32">
              <label htmlFor="position-select" className="block text-sm font-medium mb-1">
                Position
              </label>
              <Select
                inputId="position-select"
                options={positionOptions}
                value={position}
                onChange={setPosition}
                placeholder="Select Position"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
            {!showCompare && <div className="w-full sm:w-40">
              <CSVLink
                data={csvData}
                filename={`network_report_hourly_${formatDate(selectedMonth) || "report"}.csv`}
                className="w-full py-2 px-4 bg-green-500 text-white rounded-lg font-medium text-sm text-center block"
              >
                Download CSV
              </CSVLink>
            </div>}
          </div>
        </div>

        {showCompare && (
          <div className="mb-4">
            <ScrollContainer className="scroll-container">
              <table className="table table-bordered w-full text-left">
                <thead className="sticky top-0 bg-yellow-200">
                  <tr>
                    <th className="px-4 py-2 font-semibold" rowSpan={2}>
                      Hour
                    </th>
                    <th className="px-4 py-2 font-semibold text-center" colSpan={2}>
                      Request
                    </th>
                    <th className="px-4 py-2 font-semibold text-center" colSpan={2}>
                      Impressions
                    </th>
                    <th className="px-4 py-2 font-semibold text-center" colSpan={2}>
                      Revenue
                    </th>
                    
                  </tr>
                  <tr>
                    <th className="px-4 py-2 font-medium text-center">{formatDate(compareDates.date1)}</th>
                    <th className="px-4 py-2 font-medium text-center">{formatDate(compareDates.date2)}</th>
                    <th className="px-4 py-2 font-medium text-center">{formatDate(compareDates.date1)}</th>
                    <th className="px-4 py-2 font-medium text-center">{formatDate(compareDates.date2)}</th>
                    <th className="px-4 py-2 font-medium text-center">{formatDate(compareDates.date1)}</th>
                    <th className="px-4 py-2 font-medium text-center">{formatDate(compareDates.date2)}</th>
                   
                  </tr>
                </thead>
                {tableLoading ? (
                  <TableSkelton colspan={76} rowSpan={24} />
                ) : compareData.length === 2 ? (
                  <tbody>
                    {[...Array(24).keys()].map((hour) => {
                      const date1Data = compareStructuredData[0]?.data[compareStructuredData[0]?.date]?.[hour] ?? zeroHourObj();
                      const date2Data = compareStructuredData[1]?.data[compareStructuredData[1]?.date]?.[hour] ?? zeroHourObj();
                      
                      return (
                        <tr key={hour} className="group/details hover:bg-slate-100 border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">{hourLabel(hour)}</td>
                          <td className="px-4 py-2 text-center">{renderCell(date1Data.req_count, date2Data.req_count)}</td>
                          <td className="px-4 py-2 text-center">{renderCell(date2Data.req_count, date1Data.req_count)}</td>
                          <td className="px-4 py-2 text-center">{renderCell(date1Data.response_count, date2Data.response_count)}</td>
                          <td className="px-4 py-2 text-center">{renderCell(date2Data.response_count, date1Data.response_count)}</td>
                          <td className="px-4 py-2 text-center">{renderCell(date1Data.error_count, date2Data.error_count)}</td>
                          <td className="px-4 py-2 text-center">{renderCell(date2Data.error_count, date1Data.error_count)}</td>
                        
                        </tr>
                      );
                    })}
                    <tr className="font-semibold bg-gray-100">
                      <td className="px-4 py-2 text-center">Total</td>
                      {(() => {
                        const date1Totals = compareStructuredData[0]?.data[compareStructuredData[0]?.date]?.reduce(
                          (acc, hour) => ({
                            req_count: acc.req_count + (hour?.req_count ?? 0),
                            response_count: acc.response_count + (hour?.response_count ?? 0),
                            error_count: acc.error_count + (hour?.error_count ?? 0),
                          }),
                          zeroHourObj()
                        ) ?? zeroHourObj();
                        
                        const date2Totals = compareStructuredData[1]?.data[compareStructuredData[1]?.date]?.reduce(
                          (acc, hour) => ({
                            req_count: acc.req_count + (hour?.req_count ?? 0),
                            response_count: acc.response_count + (hour?.response_count ?? 0),
                            error_count: acc.error_count + (hour?.error_count ?? 0),
                          }),
                          zeroHourObj()
                        ) ?? zeroHourObj();
                        
                        return (
                          <>
                            <td className="px-4 py-2 text-center">{renderCell(date1Totals.req_count, date2Totals.req_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(date2Totals.req_count, date1Totals.req_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(date1Totals.response_count, date2Totals.response_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(date2Totals.response_count, date1Totals.response_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(date1Totals.error_count, date2Totals.error_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(date2Totals.error_count, date1Totals.error_count)}</td>
                            
                          </>
                        );
                      })()}
                    </tr>
                  </tbody>
                ) : (
                  <NoDataFound colspan={76} />
                )}
              </table>
            </ScrollContainer>
          </div>
        )}

        {!showCompare && (
          <div className="overflow-x-auto">
            <ScrollContainer className="scroll-container">
              <table className="table table-bordered w-full text-left">
                <thead className="sticky top-0 bg-yellow-200">
                  <tr>
                    <th className="px-4 py-2 font-semibold" rowSpan={2}>
                      Date
                    </th>
                    {[...Array(24).keys()].map((hour) => (
                      <th key={hour} className="px-4 py-2 font-semibold text-center" colSpan={3}>
                        {hourLabel(hour)}
                      </th>
                    ))}
                    <th className="px-4 py-2 font-semibold" colSpan={3}>
                      Total
                    </th>
                  </tr>
                  <tr>
                    {[...Array(24).keys()].map((hour) => (
                      <React.Fragment key={hour}>
                        <th className="px-4 py-2 font-medium text-center">Request</th>
                        <th className="px-4 py-2 font-medium text-center">Impressions</th>
                        <th className="px-4 py-2 font-medium text-center">Revenue</th>
                      </React.Fragment>
                    ))}
                    <th className="px-4 py-2 font-medium text-center">Request</th>
                    <th className="px-4 py-2 font-medium text-center">Impressions</th>
                    <th className="px-4 py-2 font-medium text-center">Revenue</th>
                  </tr>
                </thead>
                <>
                  {tableLoading ? (
                    <TableSkelton colspan={COLS} rowSpan={10} />
                  ) : dates.length === 0 ? (
                    <NoDataFound colspan={24} />
                  ) : (
                    <tbody>
                      {dates.map((date, rowIndex) => (
                        <tr key={date} className="group/details hover:bg-slate-100 border-b border-gray-200">
                          <td className="px-4 py-2 font-semibold">{formatDate(date*1000)}</td>
                          {[...Array(24).keys()].map((hour) => {
                            const { req_count, response_count, error_count } = structuredData[date][hour] ?? zeroHourObj();
                            return (
                              <React.Fragment key={hour}>
                                <td className="px-4 py-2 text-center">{renderCell(req_count)}</td>
                                <td className="px-4 py-2 text-center">{renderCell(response_count)}</td>
                                <td className="px-4 py-2 text-center">{renderCell(error_count)}</td>
                              </React.Fragment>
                            );
                          })}
                          <td className="px-4 py-2 text-center font-semibold">{renderCell(dateTotals[rowIndex]?.req_count)}</td>
                          <td className="px-4 py-2 text-center font-semibold">{renderCell(dateTotals[rowIndex]?.response_count)}</td>
                          <td className="px-4 py-2 text-center font-semibold">{renderCell(dateTotals[rowIndex]?.error_count)}</td>
                        </tr>
                      ))}
                      <tr className="font-semibold bg-gray-100">
                        <td className="px-4 py-2 text-center">Total</td>
                        {[...Array(24).keys()].map((hour) => (
                          <React.Fragment key={hour}>
                            <td className="px-4 py-2 text-center">{renderCell(grandTotals[hour]?.req_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(grandTotals[hour]?.response_count)}</td>
                            <td className="px-4 py-2 text-center">{renderCell(grandTotals[hour]?.error_count)}</td>
                          </React.Fragment>
                        ))}
                        <td className="px-4 py-2 text-center">{renderCell(overallTotals.req_count)}</td>
                        <td className="px-4 py-2 text-center">{renderCell(overallTotals.response_count)}</td>
                        <td className="px-4 py-2 text-center">{renderCell(overallTotals.error_count)}</td>
                      </tr>
                    </tbody>
                  )}
                </>
              </table>
            </ScrollContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkReportHourly;